import * as sensorsActions from '@Actions/sensors';

import {
	ConfigurationParams,
	PaginatedConfigurations,
} from '@Types/configurations.types';
import {
	GetSensorHistoryParams,
	GetSensorHistoryResponse,
	NewSensorDataPointsParams,
} from '@Types/editSensorData.types';
import { GetSensorsResponse, SensorParams } from '@Types/sensors.types';
import {
	editSensorDataPointsService,
	getSensorHistory as getSensorHistoryService,
	getSensor as getSensorService,
	getSensorsByType,
	getSensorsById,
} from '@Services/sensors';
import { error as notificationError, success } from '@innovyze/stylovyze';
import { all, CallEffect, put, retry, takeLatest } from 'redux-saga/effects';

import { AxiosResponse } from 'axios';
import { Configuration } from '@Types/configuration.types';
import { PayloadAction } from '@reduxjs/toolkit';
import { getConfigurations } from '@Services/configurations';
import {
	FilterVirtualSensor,
	VirtualSensor,
	VirtualSensorsCountResponse,
	PhysicalSensorsCountResponse,
	SensorsUptime,
	SensorsHealthStatus,
} from '@Types/sensor.types';
import {
	SensorsUptimeResponse,
	SensorsLatestDataRequest,
	SensorsLatestDataResponse,
	SensorsLatestData,
	SensorsUptimeDataRequest,
} from '@Types/edgeAnalytics.types';
import {
	getSensorsLatestData,
	getSensorsHealthStatusLegacy,
	getSensorsHealthStatus,
} from '@Services/edgeAnalytics';
import {
	mapLatestMeasureToFixed,
	parsePercentageValueToStatus,
} from '@Utils/sensorsAnalytics';

function* getSensorsSaga(action: PayloadAction<SensorParams>) {
	const { getSensorResolved, getSensorRejected } = sensorsActions;
	try {
		const response: AxiosResponse<GetSensorsResponse> = yield retry(
			5,
			1500,
			getSensorService,
			action.payload,
		);
		if (response.data) {
			yield put(getSensorResolved(response.data));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(getSensorRejected());
	}
}

function* getSensorHistorySaga(action: PayloadAction<GetSensorHistoryParams>) {
	const { getSensorHistoryResolved, getSensorHistoryRejected } =
		sensorsActions;
	try {
		const response: AxiosResponse<GetSensorHistoryResponse> = yield retry(
			5,
			1500,
			getSensorHistoryService,
			action.payload,
		);

		yield put(getSensorHistoryResolved(response.data));
	} catch (err) {
		yield put(getSensorHistoryRejected());
	}
}

function* editSensorDataPointsSaga(
	action: PayloadAction<NewSensorDataPointsParams[]>,
) {
	const { editSensorDataPointsResolved, editSensorDataPointsRejected } =
		sensorsActions;
	try {
		yield retry(5, 1500, editSensorDataPointsService, action.payload);
		yield put(success('Sensor points updated successfully!'));
		yield put(editSensorDataPointsResolved(action.payload));
	} catch (err) {
		yield put(
			notificationError(
				'There was an error updating the sensor points, please try again later',
			),
		);
		yield put(editSensorDataPointsRejected());
	}
}

function* getSensorIdsMapSaga(action: PayloadAction<ConfigurationParams>) {
	const { getSensorIdsMapResolved, getSensorIdsMapRejected } = sensorsActions;
	try {
		const payload: AxiosResponse<PaginatedConfigurations> = yield retry(
			5,
			1500,
			getConfigurations,
			action.payload,
		);
		const sensorPayload: AxiosResponse<{ sensors: VirtualSensor[] }> =
			yield retry(
				5,
				1500,
				getSensorsById,
				decodeURIComponent(action.payload.sensorIds ?? ''),
			);
		if (payload.data && sensorPayload.data) {
			const sensorIdsMap: { [key: string]: Configuration } = {};
			payload.data.configurations.forEach(configuration => {
				sensorIdsMap[`${configuration.sensorId}`] = configuration;
			});
			sensorPayload.data.sensors.forEach(sensor => {
				sensorIdsMap[`${sensor.sensorId}`] =
					sensor as unknown as Configuration;
			});
			yield put(getSensorIdsMapResolved(sensorIdsMap));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		yield put(getSensorIdsMapRejected());
	}
}

function* getVirtualSensorsSaga(action: PayloadAction<FilterVirtualSensor>) {
	const { getVirtualSensorsResolved, getVirtualSensorsRejected } =
		sensorsActions;
	try {
		console.log('calling virtual sensors', action.payload);
		const response: AxiosResponse<{
			sensors: VirtualSensor[];
		}> = yield retry(5, 15000, getSensorsByType, action.payload);
		if (response.data) {
			yield put(getVirtualSensorsResolved(response.data.sensors));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		yield put(getVirtualSensorsRejected());
	}
}

function* getVirtualSensorsCountSaga() {
	const { getVirtualSensorsCountResolved, getVirtualSensorsCountRejected } =
		sensorsActions;
	try {
		const response: AxiosResponse<VirtualSensorsCountResponse> =
			yield retry(5, 15000, getConfigurations, {
				virtualOrPhysical: 'virtual',
				configType: 'sensor',
				offset: 1,
				limit: 1,
			});
		if (response.data) {
			yield put(
				getVirtualSensorsCountResolved(response.data.pagination.total),
			);
		} else {
			yield put(getVirtualSensorsCountRejected());
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		yield put(getVirtualSensorsCountRejected());
	}
}

function* getPhysicalSensorsCountSaga() {
	const { getPhysicalSensorsCountResolved, getPhysicalSensorsCountRejected } =
		sensorsActions;
	try {
		const response: AxiosResponse<PhysicalSensorsCountResponse> =
			yield retry(5, 15000, getConfigurations, {
				virtualOrPhysical: 'physical',
				configType: 'sensor',
				offset: 1,
				limit: 1,
			});
		if (response.data) {
			yield put(
				getPhysicalSensorsCountResolved(response.data.pagination.total),
			);
		} else {
			yield put(getPhysicalSensorsCountRejected());
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		yield put(getPhysicalSensorsCountRejected());
	}
}

function* getSensorByIdSaga(action: PayloadAction<string>) {
	const { getSensorByIdResolved, getSensorByIdRejected } = sensorsActions;
	try {
		const response: AxiosResponse<GetSensorsResponse> = yield retry(
			5,
			1500,
			getSensorService,
			{ id: action.payload },
		);
		if (response.data) {
			yield put(getSensorByIdResolved(response.data));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(getSensorByIdRejected());
	}
}

function* getSensorsLatestDataSaga(
	action: PayloadAction<SensorsLatestDataRequest[]>,
) {
	const { getSensorsLatestDataResolved, getSensorsLatestDataRejected } =
		sensorsActions;
	try {
		const MAX_SENSORS_BATCH_LENGTH = 25;
		const allSensors = action.payload;

		// eslint-disable-next-line
		const allRequest: CallEffect<AxiosResponse<SensorsLatestDataResponse, any>>[] = [];

		let groupEnd = 0;
		let groupStart = 0;
		while (groupEnd < allSensors.length) {
			groupEnd += MAX_SENSORS_BATCH_LENGTH;
			const batch = allSensors.slice(groupStart, groupEnd);
			allRequest.push(retry(3, 1500, getSensorsLatestData, batch));
			groupStart = groupEnd;
		}

		const responses: AxiosResponse<SensorsLatestDataResponse>[] = yield all(
			allRequest,
		);
		const responsesData = responses.reduce(
			(
				acc: SensorsLatestData[],
				response: AxiosResponse<SensorsLatestDataResponse>,
			) => {
				if (response.data) acc = acc.concat(response.data.query);
				return acc;
			},
			[],
		);

		if (responsesData.length) {
			const sensorsUptimeMap: SensorsUptime = {};
			for (const sensor of responsesData) {
				sensorsUptimeMap[sensor.sensorId] = {
					latestValue: mapLatestMeasureToFixed(sensor.latestValue),
					lastValueTimestamp: sensor.latestTsMs,
				};
			}

			yield put(getSensorsLatestDataResolved(sensorsUptimeMap));
		} else {
			yield put(getSensorsLatestDataRejected());
		}
	} catch (e) {
		console.error(e);
		yield put(getSensorsLatestDataRejected());
	}
}

function* getSensorsHealthStatusSaga(
	action: PayloadAction<SensorsUptimeDataRequest[]>,
) {
	const { getSensorsHealthStatusResolved, getSensorsHealthStatusRejected } =
		sensorsActions;

	try {
		const sensorsRequest = action.payload;
		const response: AxiosResponse<SensorsUptimeResponse> = yield retry(
			5,
			1500,
			getSensorsHealthStatus,
			sensorsRequest,
		);

		if (response.data) {
			const sensorsUptimeMap: SensorsHealthStatus = {};
			const { query } = response.data;
			const queryMap = new Map(
				query.map(uptime => [uptime.sensorId, uptime]),
			);
			for (const sensor of sensorsRequest) {
				const querySensor = queryMap.get(sensor.sensorId);
				const uptime = querySensor
					? querySensor.upTimePercentages[0]
					: { value: 0 };
				const healthStatus = parsePercentageValueToStatus(uptime.value);
				sensorsUptimeMap[sensor.sensorId] = { status: healthStatus };
			}

			yield put(getSensorsHealthStatusResolved(sensorsUptimeMap));
		} else {
			console.debug('No data returned from v3 uptime');
			yield put(getSensorsHealthStatusRejected());
		}
	} catch (e) {
		console.error(e);
		yield put(getSensorsHealthStatusRejected());
	}
}

/**
 * @ deprecated
 */
function* getSensorsHealthStatusSagaLegacy(action: PayloadAction<string[]>) {
	const {
		getSensorsHealthStatusResolvedLegacy,
		getSensorsHealthStatusRejectedLegacy,
	} = sensorsActions;
	try {
		const response: AxiosResponse<SensorsUptimeResponse> = yield retry(
			3,
			1500,
			getSensorsHealthStatusLegacy,
			action.payload,
		);

		if (response.data) {
			const sensorsUptimeMap: SensorsHealthStatus = {};
			const { query } = response.data;
			for (const sensor of query) {
				const healthStatus = parsePercentageValueToStatus(
					sensor.upTimePercentages[0].value,
				);
				sensorsUptimeMap[sensor.sensorId] = {
					status: healthStatus,
					latestValue: sensor.latestValue,
				};
			}
			yield put(getSensorsHealthStatusResolvedLegacy(sensorsUptimeMap));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(getSensorsHealthStatusRejectedLegacy());
	}
}

function* watchGetSensorIdsMap() {
	yield takeLatest(sensorsActions.getSensorIdsMap, getSensorIdsMapSaga);
}

function* watchGetSensors() {
	yield takeLatest(sensorsActions.getSensor, getSensorsSaga);
}

function* watchGetSensorHistory() {
	yield takeLatest(sensorsActions.getSensorHistory, getSensorHistorySaga);
}

function* watchEditSensorDataPoints() {
	yield takeLatest(
		sensorsActions.editSensorDataPoints,
		editSensorDataPointsSaga,
	);
}

function* watchGetVirtualSensors() {
	yield takeLatest(sensorsActions.getVirtualSensors, getVirtualSensorsSaga);
}

function* watchGetVirtualSensorsCount() {
	yield takeLatest(
		sensorsActions.getVirtualSensorsCount,
		getVirtualSensorsCountSaga,
	);
}

function* watchGetPhysicalSensorsCount() {
	yield takeLatest(
		sensorsActions.getPhysicalSensorsCount,
		getPhysicalSensorsCountSaga,
	);
}

function* watchGetSensorById() {
	yield takeLatest(sensorsActions.getSensorById, getSensorByIdSaga);
}

function* watchGetSensorsLatestData() {
	yield takeLatest(
		sensorsActions.getSensorsLatestData,
		getSensorsLatestDataSaga,
	);
}

function* watchGetSensorsHealthStatus() {
	yield takeLatest(
		sensorsActions.getSensorsHealthStatus,
		getSensorsHealthStatusSaga,
	);
}

/**
 * @ deprecated
 */
function* watchGetSensorsHealthStatusLegacy() {
	yield takeLatest(
		sensorsActions.getSensorsHealthStatusLegacy,
		getSensorsHealthStatusSagaLegacy,
	);
}

export default [
	watchGetSensors(),
	watchGetSensorHistory(),
	watchEditSensorDataPoints(),
	watchGetSensorIdsMap(),
	watchGetVirtualSensors(),
	watchGetVirtualSensorsCount(),
	watchGetPhysicalSensorsCount(),
	watchGetSensorById(),
	watchGetSensorsLatestData(),
	watchGetSensorsHealthStatus(),
	watchGetSensorsHealthStatusLegacy(),
];
